/**
 * Compatibility shim: `prismic-reactjs` surface on @prismicio/react v3.
 *
 * The ported tree imports `RichText` and `Elements`. `RichText.render(field)` and
 * `<RichText render={field} />` are mapped onto `<PrismicRichText field={...} />`.
 * `Elements` exposes the legacy element-type constants used by custom serializers.
 */
import * as React from 'react';
import { PrismicRichText } from '@prismicio/react';

export const Elements = {
  heading1: 'heading1',
  heading2: 'heading2',
  heading3: 'heading3',
  heading4: 'heading4',
  heading5: 'heading5',
  heading6: 'heading6',
  paragraph: 'paragraph',
  preformatted: 'preformatted',
  strong: 'strong',
  em: 'em',
  listItem: 'list-item',
  oListItem: 'o-list-item',
  list: 'group-list-item',
  oList: 'group-o-list-item',
  image: 'image',
  embed: 'embed',
  hyperlink: 'hyperlink',
  label: 'label',
  span: 'span',
} as const;

type RichTextProps = {
  render?: any;
  field?: any;
  htmlSerializer?: any;
  linkResolver?: any;
};

function RichTextComponent({ render, field, htmlSerializer, linkResolver }: RichTextProps) {
  // Intentionally ignore the legacy prismic-reactjs `htmlSerializer`: its signature
  // is incompatible with @prismicio/react's `components`, and (worse) the ported
  // serializers return `null` for their default case — under @prismicio/react a
  // function serializer returning null means "render nothing", which blanks the
  // whole field (e.g. the LEI terms body). Render with PrismicRichText defaults so
  // content shows. `htmlSerializer` is destructured only to keep it off the element.
  void htmlSerializer;
  return <PrismicRichText field={render ?? field} linkResolver={linkResolver} />;
}

/** Static `RichText.render(field)` helper used by the legacy code. */
RichTextComponent.render = (field: any) =>
  React.createElement(PrismicRichText, { field });
RichTextComponent.asText = (field: any) =>
  Array.isArray(field) ? field.map((b: any) => b?.text ?? '').join('\n') : '';

export const RichText = RichTextComponent;
export default RichTextComponent;
